import { useEffect, useState } from 'react';
import { TextField } from '@shopify/polaris';

export default function VATinput({ id, vat, onChange, disabled, isEU, companyLand }) {
  const [valid, setValid] = useState(false);
  const [error, setError] = useState(false);
  const [preciseError, setPreciseError] = useState('');
  const [peppolCandidates, setPeppolCandidates] = useState([]);
  const normalizedVat = String(vat || '').trim();
  const isPeppolEndpointIdentifier = /^\d{4}\s*:[A-Za-z0-9]+$/.test(normalizedVat);

  useEffect(() => {
    let isCancelled = false;

    if (!normalizedVat) {
      setPeppolCandidates([]);
      return undefined;
    }

    fetch(`/api/peppol/preview?identifier=${encodeURIComponent(normalizedVat)}`)
      .then((res) => res.json())
      .then((data) => {
        if (!isCancelled) setPeppolCandidates(Array.isArray(data.candidates) ? data.candidates : []);
      })
      .catch(() => {
        if (!isCancelled) setPeppolCandidates([]);
      });

    return () => {
      isCancelled = true;
    };
  }, [normalizedVat]);

  useEffect(() => {
    let isCancelled = false;

    function resetValidation() {
      if (isCancelled) return;
      setValid(false);
      setError(false);
      setPreciseError('');
    }

    async function getVatCheck() {
      try {
        const res = await fetch(`/api/vatcheck/${encodeURIComponent(normalizedVat)}`);
        const data = await res.json();
        if (isCancelled) return;

        if (data.valid) {
          setValid(true);
          setError(false);
          setPreciseError('');
        } else {
          setValid(false);
          setError(true);
          data.message ? setPreciseError(data.message) : setPreciseError('Invalid VAT');
        }
      } catch (e) {
        if (isCancelled) return;
        setValid(false);
        setError(true);
        setPreciseError('Request not possible, please try again later');
      }
    }

    if (!normalizedVat) {
      resetValidation();
    } else if (isPeppolEndpointIdentifier) {
      // PEPPOL endpoint identifiers (e.g. 9925:...) are not VIES VAT numbers.
      resetValidation();
    } else if (isEU && companyLand !== 300) {
      getVatCheck();
    } else {
      resetValidation();
    }

    return () => {
      isCancelled = true;
    };
  }, [isEU, normalizedVat, companyLand, isPeppolEndpointIdentifier]);

  const labelAction =
    isEU && companyLand !== 300 ? { content: 'Official VIES Website', onAction: () => window.open(`https://ec.europa.eu/taxation_customs/vies/vatRequest.html`, '_blank').focus() } : null;

  return (
    <TextField
      id={id}
      maxLength={50}
      showCharacterCount
      autoComplete="off"
      label="VAT number"
      value={vat || ''}
      onChange={onChange}
      disabled={disabled}
      prefix={valid && !error ? '✅' : ''}
      error={error ? preciseError : ''}
      labelAction={labelAction}
      helpText={peppolCandidates.length ? `Peppol ID for invoices: ${peppolCandidates.join(' or ')}` : undefined}
    />
  );
}
